/**
 * Giesto site signals: the few shop events GA4's Shopify integration does not
 * already send, chosen because each drives a decision. Why bags don't become
 * orders (the bag and checkout funnel), which product lists and homepage
 * links lead to products, which menu choices lead to orders, demand we cannot meet (restock requests, searches
 * with no results), how people filter, and A/B test exposure.
 *
 * Publishes `giesto_funnel_*` customer events. The "Giesto sepet ve ödeme"
 * custom pixel (shopify-pixels/giesto-funnel.js) forwards them to GA4 next to
 * Shopify's checkout steps and checkout errors. Each event is mirrored to
 * Microsoft Clarity as an event and session tags, and carries the visitor's
 * Clarity recording link and live A/B variants (snippets/giesto-experiments.liquid).
 * Everything follows the visitor's consent and no personal data is sent; see
 * docs/site-analytics-2026-10-16.md and docs/purchase-funnel-analytics-2026-10-16.md.
 *
 * The bag is the header cart panel (giesto-header-surfaces.js), which /cart
 * also opens. That panel reports what only it knows through `giesto:funnel`
 * and `giesto:cart:loaded`; add-to-bag failures from every surface arrive as
 * Shopify's standard `shopify:cart:error`.
 */
(() => {
  if (window.__giestoFunnel) return;
  window.__giestoFunnel = true;

  const CLARITY_LINK_KEY = 'giesto:clarity';
  // A tap on a blocked checkout button is often repeated; one event per burst.
  const BLOCKED_REPEAT = 3000;
  // How long a freshly opened bag waits for the panel's cart read.
  const OPEN_WAIT = 4000;

  const host = document.getElementById('giesto-header-surfaces');
  const panel = host?.querySelector('.ghs-cart');
  const freeShippingThreshold = Number(host?.dataset.freeShippingThreshold) || 0;

  /** @type {{count: number, total: number, discounted: boolean, freeShippingLeft: number | null} | null} */
  let cart = null;
  let clarityLink = '';
  let openedAt = 0;
  let checkoutSinceOpen = false;
  let lastBlocked = 0;

  const clarity = (...args) => {
    try {
      if (typeof window.clarity === 'function') window.clarity(...args);
    } catch {
      /* analytics must never break the shop */
    }
  };

  const money = (cents) => Math.round(Number(cents) || 0) / 100;
  const text = (value, length = 100) => String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, length);

  /** The bag as /cart.js returns it. */
  const rememberCart = (data) => {
    if (!data || !Array.isArray(data.items)) return;
    const shipping = freeShippingThreshold && data.requires_shipping !== false;
    cart = {
      count: Number(data.item_count) || 0,
      total: money(data.total_price),
      discounted: Boolean((data.discount_codes || []).some((entry) => entry?.applicable) || Number(data.total_discount) > 0),
      freeShippingLeft: shipping ? money(Math.max(0, freeShippingThreshold - (Number(data.total_price) || 0))) : null,
    };
  };

  const cartFields = () =>
    cart
      ? {
          item_count: cart.count,
          cart_total: cart.total,
          free_shipping_left: cart.freeShippingLeft,
          has_discount: cart.discounted,
        }
      : {};

  const band = (total) => (total < 1000 ? '0-999' : total < 2000 ? '1000-1999' : total < 4000 ? '2000-3999' : '4000+');

  // Session tags make "had a bag, never pressed checkout" one Clarity filter.
  const tagSession = () => {
    if (!cart?.count) return;
    clarity('set', 'cart_items', cart.count < 3 ? String(cart.count) : '3+');
    clarity('set', 'cart_value', band(cart.total));
    if (cart.freeShippingLeft !== null) clarity('set', 'free_shipping', cart.freeShippingLeft === 0 ? 'yes' : 'no');
  };

  /** @param {string} name @param {Record<string, unknown>} [fields] */
  const track = (name, fields = {}) => {
    askClarity();
    const page = window.ShopifyAnalytics?.meta?.page;
    try {
      window.Shopify?.analytics?.publish?.(`giesto_funnel_${name}`, {
        ...fields,
        page_type: page?.pageType || '',
        signed_in: Boolean(page?.customerId),
        clarity_link: clarityLink,
        experiments: window.GiestoDeney?.label?.() || '',
      });
    } catch {
      /* analytics must never break the shop */
    }
    clarity('event', name);
  };

  /* ------------------------------------------------------- Clarity link -- */

  // The link is also kept for the checkout pixel, which runs where Clarity
  // cannot: Shopify's checkout on this same domain.
  const rememberClarity = (data) => {
    if (!data?.projectId || !data.userId || !data.sessionId) return;
    clarityLink = `https://clarity.microsoft.com/player/${data.projectId}/${data.userId}/${data.sessionId}`;
    try {
      localStorage.setItem(CLARITY_LINK_KEY, JSON.stringify({link: clarityLink, at: Date.now()}));
    } catch {
      /* private mode: the storefront events still carry the link */
    }
  };
  // Clarity loads after consent, usually after this script, so every event
  // asks again until it is there. `recall` keeps the link current if Clarity
  // starts a new session on this page.
  let askedClarity = false;
  function askClarity() {
    if (askedClarity || typeof window.clarity !== 'function') return;
    askedClarity = true;
    clarity('metadata', rememberClarity, false, true);
    // A/B exposure can happen before consent loads Clarity; tag it now.
    try {
      const seen = JSON.parse(sessionStorage.getItem('giesto:deneyler:seen') || '{}');
      for (const [id, variant] of Object.entries(seen)) clarity('set', `deney_${id}`, variant);
    } catch {
      /* private mode */
    }
  }
  askClarity();
  window.addEventListener('load', askClarity, {once: true});
  document.addEventListener('visitorConsentCollected', () => setTimeout(askClarity, 0));

  /* ------------------------------------------------------------ the bag -- */

  document.addEventListener('giesto:cart:loaded', (event) => rememberCart(/** @type {CustomEvent} */ (event).detail));
  document.addEventListener('giesto:cart:change', (event) => rememberCart(/** @type {CustomEvent} */ (event).detail));

  // Personal order (A/B test `kisisel-siralama`) actually changed a list:
  // once per session per surface, from snippets/giesto-affinity.liquid. Some
  // happen while the page parses, before this script runs.
  const reportPersonalOrder = () => {
    for (const surface of window.GiestoIlgi?.takeApplied?.() || []) {
      track('personal_order', {surface});
      clarity('set', 'kisisel_siralama', 'uygulandi');
    }
  };
  reportPersonalOrder();
  document.addEventListener('giesto:ilgi:uygulandi', reportPersonalOrder);

  // Things only the cart panel knows: discount answers and its own failures.
  document.addEventListener('giesto:funnel', (event) => {
    const {name, fields} = /** @type {CustomEvent} */ (event).detail || {};
    if (name === 'discount_code') {
      track('discount_code', {result: fields?.result || '', code: text(fields?.code, 32).toUpperCase()});
      if (fields?.result === 'rejected') clarity('event', 'discount_rejected');
    } else if (name === 'cart_error') {
      track('cart_error', {code: text(fields?.code, 40), message: text(fields?.message), source: 'cart_panel'});
      clarity('set', 'cart_error', 'yes');
    } else if (name === 'search_no_results') {
      noResults(fields?.term, 'search_box');
    } else if (name === 'select_item' && fields?.list === 'urun_kaydirma') {
      track('select_item', {list:'urun_kaydirma', product_handle:text(fields.product_handle, 80), product_title:text(fields.product_title, 80), position:Number(fields.position) || 0});
    } else if (name === 'stock_alert') {
      track('stock_alert', {product_handle: text(fields?.handle, 80), product_title: text(fields?.title), size: text(fields?.variant, 40)});
    }
  });

  // Add-to-bag failures from every surface: stock limits, sold-out sizes,
  // network errors.
  document.addEventListener('shopify:cart:error', (event) => {
    const error = /** @type {any} */ (event);
    track('cart_error', {
      code: text(error.code, 40),
      message: text(error.detail?.description || error.error),
      source: error.target instanceof Element && error.target.closest('.ghs-cart') ? 'cart_panel' : 'add_to_bag',
    });
    clarity('set', 'cart_error', 'yes');
  });

  // A successful add refreshes the bag and keeps the recording: Clarity drops
  // some sessions, never one that has been upgraded.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const {action, promise} = /** @type {any} */ (event);
    if (!(promise instanceof Promise)) return;
    promise
      .then((result) => {
        if (result?.detail?.didError) return;
        if (action === 'add') clarity('upgrade', 'add_to_bag');
      })
      .catch(() => {});
  });

  if (panel) {
    const onCartPage = () => /\/cart\/?$/.test(location.pathname);
    let waiting = 0;
    const announceOpen = () => {
      clearTimeout(waiting);
      document.removeEventListener('giesto:cart:loaded', announceOpen);
      if (!panel.open) return;
      track('cart_open', {...cartFields(), cart_page: onCartPage()});
      tagSession();
      if (cart?.count) clarity('upgrade', 'cart_open');
    };
    new MutationObserver(() => {
      if (panel.open && !openedAt) {
        openedAt = Date.now();
        checkoutSinceOpen = false;
        // The panel always rereads the cart on opening; report what it shows.
        document.addEventListener('giesto:cart:loaded', announceOpen);
        waiting = setTimeout(announceOpen, OPEN_WAIT);
      } else if (!panel.open && openedAt) {
        const seconds = Math.round((Date.now() - openedAt) / 1000);
        openedAt = 0;
        clearTimeout(waiting);
        document.removeEventListener('giesto:cart:loaded', announceOpen);
        if (cart?.count && !checkoutSinceOpen) {
          track('cart_close', {...cartFields(), seconds_open: seconds, cart_page: onCartPage()});
          clarity('event', 'cart_closed_with_items');
        }
      }
    }).observe(panel, {attributes: true, attributeFilter: ['open']});
  }

  /* ----------------------------------------------------------- checkout -- */

  const CHECKOUT_PATH = /\/checkouts?(?:\/|$)/;

  /** @param {Element} control */
  const sourceOf = (control) => {
    if (control.closest('.ghs-cart')) return /\/cart\/?$/.test(location.pathname) ? 'cart_page' : 'cart_panel';
    if (control.closest('giesto-store-add-toast, .gs-toast')) return 'added_toast';
    return window.ShopifyAnalytics?.meta?.page?.pageType || 'other';
  };

  // Capture phase, so this runs before a surface cancels a blocked tap.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const express = target.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
      const control = express || target.closest('a[href], button[name="checkout"], input[name="checkout"]');
      if (!control) return;
      if (control instanceof HTMLAnchorElement && !CHECKOUT_PATH.test(new URL(control.href, location.href).pathname)) return;
      const blocked = control.getAttribute('aria-disabled') === 'true' || /** @type {HTMLButtonElement} */ (control).disabled === true;
      if (blocked) {
        if (Date.now() - lastBlocked < BLOCKED_REPEAT) return;
        lastBlocked = Date.now();
      } else {
        checkoutSinceOpen = true;
        clarity('set', 'checkout_clicked', 'yes');
      }
      track('checkout_click', {...cartFields(), source: sourceOf(control), blocked, express: Boolean(express)});
      if (blocked) clarity('event', 'checkout_blocked');
    },
    true,
  );

  /* ---------------------------------------------------------- size guide -- */

  for (const dialog of document.querySelectorAll('giesto-size-guide dialog')) {
    new MutationObserver(() => {
      if (!dialog.hasAttribute('open')) return;
      const product = window.ShopifyAnalytics?.meta?.product;
      track('size_guide_open', {product_id: String(product?.id || ''), product_title: text(document.title.replace(/\s+[–-]\s+GIESTO$/, ''))});
    }).observe(dialog, {attributes: true, attributeFilter: ['open']});
  }

  /* ----------------------------------------------------------- discovery -- */

  const PRODUCT_PATH = /\/products\/([^/?#]+)/;
  // The collection menu, and the same menu shown open on the homepage, fw27 and basic.
  const MENU = '#GiestoEditorialMenu, .geh-home-nav';
  const pageType = () => window.ShopifyAnalytics?.meta?.page?.pageType || '';
  const handleOf = (link) => decodeURIComponent(new URL(link.href, location.href).pathname.match(PRODUCT_PATH)?.[1] || '');
  const labelOf = (link) => text(link.getAttribute('aria-label') || link.textContent || link.querySelector('img')?.alt, 80);

  // The list a product link sits in, so GA4 can rank lists and positions.
  const listOf = (link) => {
    if (link.closest('#giesto-header-surfaces .ghs-search')) {
      const source = link.closest('[data-ghs-results]')?.dataset.ghsSource;
      return source === 'bestsellers' ? 'arama_cok_satanlar' : source === 'search' ? 'arama_anlik_sonuclar' : 'arama_kutusu';
    }
    if (link.closest('.ghs-cart__items')) return 'sepet_urunleri';
    // One bag rail, two contents: recommendations for a bag with items (the
    // `sepet-oneri` A/B test), recently viewed for an empty one.
    const recent = link.closest('[data-ghs-recent]');
    if (recent) return recent.dataset.ghsRecentKind === 'oneri' ? 'sepet_onerileri' : 'son_bakilanlar';
    if (link.closest('#giesto-header-surfaces .ghs-cart')) return 'sepet_paneli';
    if (link.closest('giesto-store-add-toast, .gs-toast')) return 'eklendi_onerileri';
    if (link.closest(MENU)) return 'menu';
    const page = pageType();
    if (page === 'collection') return `koleksiyon:${decodeURIComponent(location.pathname.split('/collections/')[1]?.split('/')[0] || '')}`;
    if (page === 'searchresults') return 'arama_sayfasi';
    if (page === 'home') return 'anasayfa';
    if (link.closest('[data-zp-pairings], [data-gar-source="pairings"]')) return 'kombin_onerileri';
    if (page === 'product') return 'urun_onerileri';
    return page || 'diger';
  };

  // Position among the distinct products shown in the same section or panel.
  // A card often links twice (photo and title), and hidden copies for other
  // screen sizes do not count.
  const positionOf = (link, selector) => {
    // A product rail counts on its own: the pairing rail sits inside the
    // product section, beside other product links.
    const box = link.closest('.gar, [id^="shopify-section-"], .ghs-search, .ghs-cart, giesto-store-add-toast') || document.body;
    const shown = [...box.querySelectorAll(selector)].filter((item) => item === link || item.offsetParent !== null);
    const keys = [...new Set(shown.map((item) => item.getAttribute('href')))];
    return keys.indexOf(link.getAttribute('href')) + 1;
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('a[href]');
      // The product's own block is skipped, except its pairing rail.
      if (!link || (link.closest('.gap') && !link.closest('[data-zp-pairings]')) || link.closest('.giesto-stock-alert, header nav, footer')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      if (PRODUCT_PATH.test(url.pathname)) {
        // personal: the list was reordered by the visitor's taste (A/B test
        // `kisisel-siralama`, group B with a profile), so clicks can be split.
        track('select_item', {list: listOf(link), position: positionOf(link, 'a[href*="/products/"]'), product_handle: handleOf(link), product_title: labelOf(link), personal: Boolean(link.closest('[data-ilgi-sirali]'))});
      } else if (pageType() === 'home' && link.closest('main') && !link.closest('#giesto-header-surfaces')) {
        // Homepage banners and editorial links: which ones lead people in.
        track('select_promotion', {destination: text(url.pathname, 80), creative: labelOf(link), slot: positionOf(link, 'main a[href]:not([href*="/products/"])')});
      }
    },
    true,
  );

  /* ---------------------------------------------------------------- menu -- */

  // The collection menu: how often it opens, which of its links people take,
  // and which choice an add to bag, checkout or order followed. The last
  // choice is kept for the pixel, which attaches it as `menu_secimi` while it
  // is under two hours old (shopify-pixels/giesto-funnel.js).
  const MENU_CHOICE_KEY = 'giesto:menu';
  const menuDialog = document.getElementById('GiestoEditorialMenu');
  const menuArea = (link) => {
    if (link.closest('.geh-tiles')) return 'one_cikanlar';
    if (link.closest('.geh-primary')) return 'ana_kategori';
    if (link.closest('.geh-categories')) return 'alt_kategori';
    if (link.closest('.ge-story')) return 'hikaye';
    return 'diger';
  };
  // Tiles and main categories have fixed codes in both languages; other links
  // go by their label. Turkish lowercasing turns an upper-case I into ı, so
  // "T-SHIRT" and "T-Shirt" would differ: ı is folded to i.
  const menuCode = (link) => link.dataset.menuKodu || link.dataset.gehCategory || labelOf(link).toLocaleLowerCase('tr').replace(/ı/g, 'i').replace(/\s+/g, '_').slice(0, 40);

  if (menuDialog) {
    new MutationObserver(() => {
      if (menuDialog.open) track('menu_open', {});
    }).observe(menuDialog, {attributes: true, attributeFilter: ['open']});
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link?.closest(MENU)) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      const area = menuArea(link);
      const code = menuCode(link);
      const box = link.closest('.geh-tiles, .geh-primary, .geh-categories, .ge-story') || link.closest(MENU);
      const shown = [...box.querySelectorAll('a[href]')].filter((item) => item === link || item.offsetParent !== null);
      track('menu_click', {area, code, destination: text(url.pathname + url.search, 100), label: labelOf(link), position: shown.indexOf(link) + 1});
      clarity('set', 'menu_alani', area);
      try {
        localStorage.setItem(MENU_CHOICE_KEY, JSON.stringify({choice: `${area}:${code}`, at: Date.now()}));
      } catch {
        /* private mode: the click itself is still reported */
      }
    },
    true,
  );

  /* ------------------------------------------------- searches with no result -- */

  // The search box answers while people type; only a query left standing for
  // two seconds counts, and partial words under three letters never do.
  let noResultsTimer = 0;
  function noResults(term, source) {
    const value = text(term, 60).toLocaleLowerCase('tr');
    if (value.length < 3) return;
    clearTimeout(noResultsTimer);
    noResultsTimer = setTimeout(() => track('search_no_results', {term: value, source}), source === 'search_box' ? 2000 : 0);
  }
  if (pageType() === 'searchresults') {
    const count = Number.parseInt(document.querySelector('.gac__count')?.textContent || '', 10);
    if (count === 0) noResults(new URLSearchParams(location.search).get('q'), 'search_page');
  }

  /* ------------------------------------------------------------- filters -- */

  // Which filters people use on collections and search. Facets rewrite the
  // URL in place and the sort menu reloads the page, so both are read from
  // the URL: a newly added `filter.*` or `sort_by` value counts once.
  const FILTER_KEY = /^(sort_by$|filter\.)/;
  const filterName = (key) => {
    if (key === 'sort_by') return 'siralama';
    if (key.includes('.price')) return 'fiyat';
    if (key.startsWith('filter.v.option.')) return text(key.slice('filter.v.option.'.length), 30);
    return text(key.split('.').pop(), 30);
  };
  const filterState = (search) => {
    const state = new Map();
    for (const [key, value] of new URLSearchParams(search)) if (FILTER_KEY.test(key)) state.set(`${key}=${value}`, [key, value]);
    return state;
  };
  const reportFilters = (before, after) => {
    for (const [entry, [key, value]] of after) {
      if (!before.has(entry)) track('filter_used', {filter: filterName(key), value: key.includes('.price') ? '' : text(value, 40)});
    }
  };
  if (['collection', 'searchresults'].includes(pageType())) {
    let current = filterState(location.search);
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin === location.origin && referrer.pathname === location.pathname) reportFilters(filterState(referrer.search), current);
    } catch {
      /* no referrer */
    }
    const check = () => {
      const next = filterState(location.search);
      reportFilters(current, next);
      current = next;
    };
    for (const method of ['pushState', 'replaceState']) {
      const original = history[method];
      history[method] = function (...args) {
        const result = original.apply(this, args);
        queueMicrotask(check);
        return result;
      };
    }
    window.addEventListener('popstate', check);
  }
})();
